import { apiFetch } from './api';
import {
  invalidateCachedResource,
  invalidateCachedResourcesByPrefix,
  readCachedResource,
} from './requestCache';

export type ClientDashboardSummary = {
  orgId: string;
  organizationName: string;
  organizationLogoUrl?: string | null;
  accountHealth: {
    contractedMonthlyMinutesMin: number | null;
    contractedMonthlyMinutesMax: number | null;
    consumedBillableMinutes: number;
    blockedBillableMinutes: number;
    plannedRemainingEstimateMinutes: number;
    remainingBillableMinutesMin: number | null;
    remainingBillableMinutesMax: number | null;
    withinTargetCount: number;
    outsideTargetCount: number;
  };
  meetingSummary?: {
    totalIncludedMeetings: number;
    totalUsedMeetings: number;
    totalRemainingMeetings: number;
    unassignedMeetingsInWindow: number;
    contracts: Array<{
      serviceContractId: string;
      serviceCode: string | null;
      serviceName: string;
      tierName: string | null;
      tierSkuId: string | null;
      cadenceLabel: string;
      window: 'month' | 'quarter';
      windowLabel: string;
      periodStartAt: string;
      periodEndAt: string;
      includedMeetings: number;
      usedMeetings: number;
      remainingMeetings: number;
    }>;
  };
  utilizationDirection: {
    totalProjectedMinutes: number;
    outsideProjectMinutes?: number;
    outsideProjectTickets?: number;
    laneCount: number;
    lanes: Array<{
      id: string;
      name: string;
      estimatedMinutes: number;
      ticketCount: number;
      share: number;
    }>;
  };
};

type ClientDashboardFetchOptions = {
  force?: boolean;
};

const CLIENT_DASHBOARD_CACHE_TTL_MS = 60_000;
const CLIENT_DASHBOARD_CACHE_PREFIX = 'client-dashboard-summary:';

function buildClientDashboardCacheKey(orgId?: string | null): string {
  const scope = orgId?.trim() || 'self';
  return `${CLIENT_DASHBOARD_CACHE_PREFIX}${scope}`;
}

export function invalidateClientDashboardCache(orgId?: string | null): void {
  if (orgId?.trim()) {
    invalidateCachedResource(buildClientDashboardCacheKey(orgId));
    return;
  }
  invalidateCachedResourcesByPrefix(CLIENT_DASHBOARD_CACHE_PREFIX);
}

export async function fetchClientDashboardSummary(
  orgId?: string | null,
  options: ClientDashboardFetchOptions = {},
): Promise<ClientDashboardSummary> {
  const cacheKey = buildClientDashboardCacheKey(orgId);
  return readCachedResource(
    cacheKey,
    async () => {
      const params = new URLSearchParams();
      if (orgId?.trim()) params.set('orgId', orgId.trim());
      const query = params.toString();
      return apiFetch<ClientDashboardSummary>(
        `/api/projects/client-dashboard-summary${query ? `?${query}` : ''}`,
      );
    },
    {
      force: options.force,
      ttlMs: CLIENT_DASHBOARD_CACHE_TTL_MS,
    },
  );
}
